import * as THREE from 'three';
import { EffectType } from '@/types/effects';

export interface EffectDefinition {
  id: EffectType;
  label: string;
  description: string;
  textures: {
    diffuse?: string;
    normal?: string;
    roughness?: string;
    displacement?: string;
    alpha?: string;  // For alpha map support
    metalness?: string;
    diffuse_secondary?: string; // For dual-material effects (e.g. Enamel Pin fill)
    normal_secondary?: string;
    roughness_secondary?: string;
  };
  materialProps: {
    roughness: number;
    metalness: number;
    bumpScale?: number;
    opacity?: number;
    blending?: THREE.Blending;
  };
}

export const Effects: Record<EffectType, EffectDefinition> = {
  none: {
    id: 'none',
    label: 'Image As is',
    description: 'Flat, digital print with standard finish.',
    textures: {},
    materialProps: {
      roughness: 1.0,
      metalness: 0.0,
      bumpScale: 0.0
    }
  },
  embroidery: {
    id: 'embroidery',
    label: 'Embroidery',
    description: 'Stitched thread effect with realistic depth.',
    textures: {
      diffuse: '/Textures/Embroidery1/Diffusion.png',
      normal: '/Textures/Embroidery1/Normal_Map.png',
      roughness: '/Textures/Embroidery1/Roughness.png'
    },
    materialProps: {
      roughness: 0.9, // Matte finish to remove shine
      metalness: 0.0,
      bumpScale: 10.0 // Extreme depth requested
    }
  },
  sublimation: {
    id: 'sublimation',
    label: 'Sublimation',
    description: 'Ink dyed directly into the material. Smooth finish.',
    textures: {},
    materialProps: {
      roughness: 1.0, // Matte, inherits fabric texture
      metalness: 0.0,
      bumpScale: 0.0
    }
  },
  screen_print: {
    id: 'screen_print',
    label: 'Screen Print',
    description: 'Thick ink sitting on top of the surface.',
    textures: {},
    materialProps: {
      roughness: 0.8, // Reduced gloss
      metalness: 0.0,
      bumpScale: 0.5 // Thicker raised plateau bump
    }
  },
  glass_etching: {
    id: 'glass_etching',
    label: 'Glass Etching',
    description: 'Frosted, matte finish on glass surfaces.',
    textures: {
      diffuse: '/Textures/Glass/Diffuse_Map.jpg',
      normal: '/Textures/Glass/NormalMap_1024x1024_ThiagoCarneiro.com.png',
      roughness: '/Textures/Glass/RoughnessMap_1024x1024_ThiagoCarneiro.com.png'
    },
    materialProps: {
      roughness: 1.0, // Fully matte/sandblasted
      metalness: 0.0,
      bumpScale: -2.0 // Deep engraving
    }
  },
  fabric_embossed: {
    id: 'fabric_embossed',
    label: 'Fabric Debossed',
    description: 'Design pressed into fabric texture.',
    textures: {
      diffuse: '/Textures/Fabric019_2K-JPG/Fabric019_2K-JPG_Color.jpg',
      normal: '/Textures/Fabric019_2K-JPG/Fabric019_2K-JPG_NormalGL.jpg',
      roughness: '/Textures/Fabric019_2K-JPG/Fabric019_2K-JPG_Roughness.jpg',
      displacement: '/Textures/Fabric019_2K-JPG/Fabric019_2K-JPG_Displacement.jpg'
    },
    materialProps: {
      roughness: 0.9,
      metalness: 0.0,
      bumpScale: 0.0 // Flat
    }
  },
  leather_debossed: {
    id: 'leather_debossed',
    label: 'Leather Debossed',
    description: 'Design pressed into leather.',
    textures: {
      diffuse: '/Textures/Leather/leather_red_02_Difuse1_1k.jpg',
      normal: '/Textures/Leather/leather_red_02_nor_gl_1k.jpg',
      roughness: '/Textures/Leather/leather_red_02_rough_1k.jpg',
      displacement: '/Textures/Leather/leather_red_02_disp_1k.jpg'
    },
    materialProps: {
      roughness: 0.9,
      metalness: 0.0,
      bumpScale: -4.0 // Strongly pressed/deep deboss
    }
  },

  pad_print: {
    id: 'pad_print',
    label: 'Pad Print',
    description: 'Ink transferred from silicone pad. Smooth, slight texture.',
    textures: {},
    materialProps: {
      roughness: 0.9, // Matte finish
      metalness: 0.0,
      bumpScale: 1.5 // Thicker ink build-up
    }
  },

  laser_engraving: {
    id: 'laser_engraving',
    label: 'Laser Engraving',
    description: 'Precision beam etched into surface. Reveals raw metal.',
    textures: { 
        roughness: '/Textures/Glossy Metal048A_1K-JPG/Metal048A_1K-JPG_Roughness.jpg' // Borrowed from generic metal
    },
    materialProps: {
      roughness: 1.0, // Fully Matte / Rough (No Shine)
      metalness: 1.0, // Fully Metallic
      bumpScale: -0.8 // Deep etch
    }
  },

  gold_foil: {
    id: 'gold_foil',
    label: 'Gold Foil',
    description: 'Metallic gold foil application.',
    textures: {
      diffuse: '/Textures/Glossy Metal048A_1K-JPG/Metal048A_1K-JPG_Color.jpg',
      normal: '/Textures/Glossy Metal048A_1K-JPG/Metal048A_1K-JPG_NormalGL.jpg',
      roughness: '/Textures/Glossy Metal048A_1K-JPG/Metal048A_1K-JPG_Roughness.jpg',
      metalness: '/Textures/Glossy Metal048A_1K-JPG/Metal048A_1K-JPG_Metalness.jpg',
      displacement: '/Textures/Glossy Metal048A_1K-JPG/Metal048A_1K-JPG_Displacement.jpg'
    },
    materialProps: {
      roughness: 0.2,
      metalness: 1.0,
      bumpScale: 0.1
    }
  }
};
